import { Calendar, Building2, Heart, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { heroAvatars, heroImages } from '../mock';
import { useI18n } from '../contexts/I18nContext';
import { usePreviewValue } from '../contexts/CMSPreviewContext';
import HappeningNext from './HappeningNext';
import { JoySticker } from './JoyAccents';

export default function Hero() {
  const { t } = useI18n();
  const badge = usePreviewValue('hero', 'badge', t('hero.badge'));
  const tagline = usePreviewValue('hero', 'tagline', t('hero.tagline'));
  const description = usePreviewValue('hero', 'description', t('hero.desc'));
  const cta1 = usePreviewValue('hero', 'cta1_label', t('hero.cta1'));
  const cta2 = usePreviewValue('hero', 'cta2_label', t('hero.cta2'));
  const cta3 = usePreviewValue('hero', 'cta3_label', t('hero.cta3'));
  const servedCount = usePreviewValue('hero', 'served_count', '2,500+');
  const servedText = usePreviewValue('hero', 'served_text', t('hero.served'));
  const yearsImpact = usePreviewValue('hero', 'years_impact', '15+');

  return (
    <section id="top" className="relative pt-[120px] pb-20 lg:pt-[160px] lg:pb-28 overflow-hidden">
      <div className="absolute top-32 left-4 lg:left-16 w-20 h-20 rounded-full bg-[#FCD968] opacity-90 animate-float" />
      <div className="absolute top-44 right-8 lg:right-24 w-16 h-16 rounded-lg bg-[#5DCAB5]/70 rotate-12 animate-float" style={{ animationDelay: '1.5s' }} />
      <div className="absolute bottom-12 left-1/3 w-3 h-3 rounded-full bg-[#E5614C] opacity-60" />

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
        <div className="animate-fade-up">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white shadow-[0_8px_24px_-12px_rgba(42,27,26,0.15)] border border-[#2A1B1A]/5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#E5614C] opacity-75 pulse-dot" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E5614C]" />
              </span>
              <span className="text-[13px] font-semibold text-[#2A1B1A] tracking-wide">{badge}</span>
            </div>
            <HappeningNext />
          </div>
          <p className="mt-3 text-[13.5px] font-semibold uppercase tracking-[0.14em] text-[#5DCAB5]">
            {t('hero.seasonLine')}
          </p>

          <h1 className="font-serif-display mt-6 text-[56px] sm:text-[68px] lg:text-[82px] leading-[0.95] font-bold text-[#2A1B1A]">
            GEMS <span className="text-[#E5614C] italic font-serif-display">@</span> The Arts
          </h1>

          <p className="mt-4 text-[24px] lg:text-[28px] font-serif-display font-medium text-[#5DCAB5] italic">
            {tagline}
          </p>

          <p className="mt-6 text-[17px] leading-relaxed text-[#2A1B1A]/70 max-w-xl">
            {description}
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-7 h-12 shadow-[0_10px_24px_-8px_rgba(229,97,76,0.6)] btn-press">
              <a href="#events"><Calendar className="h-4 w-4 mr-2" /> {cta1}</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-2 border-[#E5614C] text-[#E5614C] hover:bg-[#E5614C] hover:text-white bg-transparent font-semibold px-7 h-12 btn-press">
              <Link to="/rentals"><Building2 className="h-4 w-4 mr-2" /> {cta2}</Link>
            </Button>
            <Button asChild className="rounded-full bg-[#5DCAB5] hover:bg-[#4ab39d] text-white font-semibold px-7 h-12 shadow-[0_10px_24px_-8px_rgba(93,202,181,0.6)] btn-press">
              <Link to="/donate"><Heart className="h-4 w-4 mr-2" /> {cta3}</Link>
            </Button>
          </div>

          <div className="mt-10 flex items-center gap-4">
            <div className="flex -space-x-3">
              {heroAvatars.map((src, i) => (
                <img key={i} src={src} alt={`GEMS @ The Arts community member ${i + 1}`} className="h-11 w-11 rounded-full border-[3px] border-[#FAF3EE] object-cover shadow-sm" />
              ))}
            </div>
            <p className="text-[15px] text-[#2A1B1A]/75">
              <span className="font-bold text-[#2A1B1A]">{servedCount}</span> {servedText}
            </p>
          </div>
        </div>

        <div className="relative h-[560px] lg:h-[640px]">
          <div className="absolute -top-3 left-2 z-10 hidden sm:block">
            <JoySticker rotate={-8} color="yellow">{t('hero.sticker')}</JoySticker>
          </div>
          <div className="absolute top-0 left-0 w-[55%] h-[58%] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(42,27,26,0.25)] hover:scale-[1.02] transition-transform duration-500">
            <img src={heroImages[0]} alt={t('hero.img1.alt')} className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-2 right-0 w-[48%] h-[42%] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(42,27,26,0.25)] hover:scale-[1.02] transition-transform duration-500">
            <img src={heroImages[1]} alt={t('hero.img2.alt')} className="w-full h-full object-cover" />
          </div>
          <div className="absolute bottom-0 left-2 w-[50%] h-[40%] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(42,27,26,0.25)] hover:scale-[1.02] transition-transform duration-500">
            <img src={heroImages[2]} alt={t('hero.img3.alt')} className="w-full h-full object-cover" />
          </div>
          <div className="absolute bottom-4 right-0 w-[45%] h-[48%] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(42,27,26,0.25)] hover:scale-[1.02] transition-transform duration-500">
            <img src={heroImages[3]} alt={t('hero.img4.alt')} className="w-full h-full object-cover" />
          </div>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white rounded-2xl px-5 py-3.5 shadow-[0_20px_40px_-12px_rgba(42,27,26,0.18)] flex items-center gap-3 animate-float">
            <div className="h-11 w-11 rounded-xl bg-[#FCD968] flex items-center justify-center">
              <Star className="h-5 w-5 text-[#2A1B1A]" fill="#2A1B1A" />
            </div>
            <div>
              <div className="font-serif-display font-bold text-[22px] leading-none text-[#2A1B1A]">{yearsImpact}</div>
              <div className="text-[12px] text-[#2A1B1A]/65 mt-0.5">{t('hero.years')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
